import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, QueryList, ViewChildren, Renderer2, OnInit, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';

interface CSRInitiative {
  id: number;
  title: string;

  // OLD format (plain text)
  description?: string;

  // NEW format (with links)
  descriptionParts?: {
    text?: string;
    linkText?: string;
    url?: string;
  }[];

  heroes: string[];
  imagePath: string;
  gradientClass: string;
  background: string;
}
interface SDGGoal {
  id: number;
  number: string;
  title: string;
  subtitle: string;
  blueIconImage: string;
  orangeCircleImage: string;
}
interface ImpactMetric {
  id: number;
  type: string;
  title: string;
  metrics: string[];
  imagePath: string;
  textColor: string;
}
@Component({
  selector: 'app-social-responsibility',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './social-responsibility.component.html',
  styleUrl: './social-responsibility.component.scss'
})
export class SocialResponsibilityComponent implements AfterViewInit, OnDestroy {
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('philosophySection') philosophySection!: ElementRef;
  @ViewChild('sdgSection') sdgSection!: ElementRef;
  @ViewChild('impactSection') impactSection!: ElementRef;
  @ViewChildren('impactCard') impactCards!: QueryList<ElementRef>;
  @ViewChildren('initiativeCard') initiativeCards!: QueryList<ElementRef>;
  @ViewChild('initiativesSection') initiativesSection!: ElementRef;

  isPhilosophyVisible = false;
  isSdgVisible = false;
  isImpactVisible = false;
  private observer: IntersectionObserver | null = null;
  private jsonLdScript: any;

  constructor(private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document) { }
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }

  scrollToInitiatives(): void {
    if (this.initiativesSection) {
      this.initiativesSection.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    this.setupScrollAnimation();
    this.setupImpactTiltEffect();
    this.setupInitiativeCardAnimation();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  injectSchemaMarkup(): void {
    const csrSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/aboutcompany/kalpita-csr#webpage",
          "url": "https://www.kalpitatechnologies.com/aboutcompany/kalpita-csr",
          "name": "Corporate Social Responsibility | Kalpita Technologies",
          "description": "Discover Kalpita Technologies' CSR initiatives — from blood donation camps and education empowerment to women's welfare and sustainable workplace practices. Aligned with 6 UN SDGs, impacting 100+ lives, 300+ children, and 10+ families.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "primaryImageOfPage": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/about-company/social-responsibility/csr-herovideo.webm"
          },
          "inLanguage": "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/aboutcompany/kalpita-csr#breadcrumbs",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "About Company", "item": "https://www.kalpitatechnologies.com/aboutcompany" },
            { "@type": "ListItem", "position": 3, "name": "Corporate Social Responsibility", "item": "https://www.kalpitatechnologies.com/aboutcompany/kalpita-csr" }
          ]
        },
        {
          "@type": "Article",
          "@id": "https://www.kalpitatechnologies.com/aboutcompany/kalpita-csr#csr-article",
          "url": "https://www.kalpitatechnologies.com/aboutcompany/kalpita-csr",
          "headline": "Kalpita's Commitment to Corporate Social Responsibility",
          "description": "Kalpita Technologies' CSR page detailing the company's mission, philosophy, UN SDG alignment, six core initiatives, and measurable community impact.",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "datePublished": "2025-01-01",
          "articleBody": "At Kalpita Technologies, CSR is not just an initiative — it is a responsibility and a culture of giving back. Together with our people, partners, and communities, we strive to build healthier, empowered, and sustainable societies..."
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/aboutcompany/kalpita-csr#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What CSR initiatives does Kalpita Technologies run?",
              "acceptedAnswer": { "@type": "Answer", "text": "Kalpita Technologies runs six core CSR initiatives: Unsung Heroes Program, Healthcare & Blood Donation Camps, Women Empowerment, Clothes drive, Education support, and Sustainable Workplace practices." }
            },
            {
              "@type": "Question",
              "name": "Which UN Sustainable Development Goals does Kalpita Technologies support?",
              "acceptedAnswer": { "@type": "Answer", "text": "Kalpita Technologies aligns its CSR efforts with six UN SDGs: SDG 2, SDG 3, SDG 4, SDG 5, SDG 8, and SDG 13." }
            }
          ]
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(csrSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  setupInitiativeCardAnimation(): void {
    const cardObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Add 'start-animation' class to the specific card that is currently in view
          this.renderer.addClass(entry.target, 'start-animation');
          // Stop observing this card so it doesn't animate again
          cardObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% of the card is visible
    });

    // Observe each card in the QueryList
    this.initiativeCards.forEach(card => {
      cardObserver.observe(card.nativeElement);
    });
  }

  setupScrollAnimation(): void {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          // Trigger animation when section is visible
          if (this.philosophySection && entry.target === this.philosophySection.nativeElement) {
            this.isPhilosophyVisible = true;
            this.observer?.unobserve(entry.target);
          }

          // Add Logic for SDG Section
          if (this.sdgSection && entry.target === this.sdgSection.nativeElement) {
            this.isSdgVisible = true;
            this.observer?.unobserve(entry.target);
          }

          // Add Observer for Impact Section
          if (this.impactSection && entry.target === this.impactSection.nativeElement) {
            this.isImpactVisible = true;
            this.observer?.unobserve(entry.target);
          }

        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% of section is visible
    });

    if (this.philosophySection) this.observer.observe(this.philosophySection.nativeElement);
    if (this.sdgSection) this.observer.observe(this.sdgSection.nativeElement);
    if (this.impactSection) this.observer.observe(this.impactSection.nativeElement);
  }

  setupImpactTiltEffect(): void {
    // Subscribe to changes in case the list updates, though typically static here
    this.impactCards.changes.subscribe(() => this.attachTiltListeners());
    this.attachTiltListeners();
  }

  attachTiltListeners(): void {
    this.impactCards.forEach((cardRef) => {
      const card = cardRef.nativeElement;

      const handleMove = (e: MouseEvent) => {
        const rect = card.getBoundingClientRect();
        // Calculate mouse position relative to the card center
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 8;

        // Calc rotation (Reverse Y for natural feel: mouse up = tilt back)
        const rotateX = ((centerY - y) / centerY) * maxRotate;
        const rotateY = ((x - centerX) / centerX) * maxRotate;

        // Apply transform via requestAnimationFrame for smoothness
        requestAnimationFrame(() => {
          card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
        });
      };

      const handleLeave = () => {
        requestAnimationFrame(() => {
          // Reset to flat
          card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
        });
      };

      card.addEventListener('mousemove', handleMove);
      card.addEventListener('mouseleave', handleLeave);
    });
  }

  csrInitiatives: CSRInitiative[] = [
    {
      id: 1,
      title: 'Hero Program: Unsung Heroes',
      description: 'We celebrate individuals who quietly transform lives in their communities. Through the Unsung Heroes Program, Kalpita recognizes and amplifies these inspiring stories, encouraging others to join in the journey of change.',
      heroes: [
        'Mr. Yashwanth - Healthcare worker',
        'Ms. Vincy – Nurse',
        'Mrs. Beulah John - Founder of The Refuge Foundation'
      ],
      imagePath: '../../../../assets/about-company/social-responsibility/csr-initiatives/unsung-heroes.png',
      gradientClass: 'blue-gradient',
      background: 'blue-background'
    },
    {
      id: 2,
      title: 'Healthcare & Blood Donation Camps',
      descriptionParts: [
        { text: 'Partnering with the ' },
        {
          linkText: 'Rotary Club of Bangalore',
          url: 'https://rcb1934.in/'
        },
        {
          text: ', Kalpita has consistently organized blood donation camps, saving hundreds of lives every year. Our Kalpitians actively participate in these drives, reinforcing our commitment to community health. Promoting healthcare remains one of our most impactful CSR pillars.'
        }
      ],
      heroes: [],
      imagePath: '../../../../assets/about-company/social-responsibility/csr-initiatives/blood-donation.png',
      gradientClass: 'orange-gradient',
      background: 'orange-background'
    },
    {
      id: 3,
      title: 'Women Empowerment',
      descriptionParts: [
        { text: 'On International Women’s Day 2025, we extended our support to girl children at' },
        {
          linkText: 'The Refuge Foundation.',
          url: 'https://therefugefoundation.in/'
        },
        {
          text: ' Our employees actively contributed by donating both monetary support and useful resources such as notebooks and learning essentials. This initiative not only uplifted young girls but also reaffirmed our commitment to advancing gender equality and women empowerment.'
        }
      ],
      heroes: [
        // 'Dr. Suresh - Mobile Clinic Coordinator',
        // 'Ms. Anjali - Community Health Worker',
        // 'Mr. Kumar - Medical Supplies Manager'
      ],
      imagePath: '../../../../assets/about-company/social-responsibility/csr-initiatives/womensday.png',
      gradientClass: 'blue-gradient',
      background: 'blue-background'
    },
    {
      id: 4,
      title: 'Clothes & Essentials Drive',
      description: 'Our Kalpitians came together to collect clothes, essentials, and ration items for families in need. The clothes drive, led by our employees in partnership with Ms. Christina, a local changemaker impacting real lives, ensured that underprivileged individuals received not just material support, but also hope through the kindness of our people.',
      heroes: [
        // 'Dr. Suresh - Mobile Clinic Coordinator',
        // 'Ms. Anjali - Community Health Worker',
        // 'Mr. Kumar - Medical Supplies Manager'
      ],
      imagePath: '../../../../assets/about-company/social-responsibility/csr-initiatives/women-empowerment.png',
      gradientClass: 'orange-gradient',
      background: 'orange-background'
    },
    {
      id: 5,
      title: 'Education & Empowerment',
      descriptionParts: [
        { text: 'We are committed to empowering future generations through education. From supporting refugee girl children with resources to partnering with ' },
        {
          linkText: 'Akshaya Patra’s',
          url: 'https://www.akshayapatra.org/'
        },
        {
          text: ' Mid-Day Meal Scheme, we’re enabling access to both nutrition and education.'
        }
      ],
      heroes: [
        // 'Dr. Suresh - Mobile Clinic Coordinator',
        // 'Ms. Anjali - Community Health Worker',
        // 'Mr. Kumar - Medical Supplies Manager'
      ],
      imagePath: '../../../../assets/about-company/social-responsibility/csr-initiatives/education-empowerment.png',
      gradientClass: 'blue-gradient',
      background: 'blue-background'
    },
    {
      id: 6,
      title: 'Sustainable Workplace ',
      description: 'Sustainability is at our core. Within our offices, we follow eco-conscious practices such as maintaining indoor plants to improve air quality, installing automatic motion-sensor lights in washrooms to reduce unnecessary power usage, and using energy-efficient LED lighting throughout our workspace. We also encourage mindful practices like manually switching off lights and air conditioning when not required, further contributing to energy savings.',
      heroes: [
        // 'Dr. Suresh - Mobile Clinic Coordinator',
        // 'Ms. Anjali - Community Health Worker',
        // 'Mr. Kumar - Medical Supplies Manager'
      ],
      imagePath: '../../../../assets/about-company/social-responsibility/csr-initiatives/Sustainable-Workplace.png',
      gradientClass: 'orange-gradient',
      background: 'orange-background'
    }
  ];

  sdgGoals: SDGGoal[] = [
    {
      id: 1,
      number: 'SDG 2',
      title: 'Zero Hunger',
      subtitle: '',
      blueIconImage: '../../../../assets/about-company/social-responsibility/zero-hunger.png',
      orangeCircleImage: '../../../../assets/about-company/social-responsibility/orange-icon.png'
    },
    {
      id: 2,
      number: 'SDG 3',
      title: 'Good Health &',
      subtitle: 'Well-Being',
      blueIconImage: '../../../../assets/about-company/social-responsibility/good-health.png',
      orangeCircleImage: '../../../../assets/about-company/social-responsibility/orange-icon.png'
    },
    {
      id: 3,
      number: 'SDG 4',
      title: 'Quality Education',
      subtitle: '',
      blueIconImage: '../../../../assets/about-company/social-responsibility/quality-education.png',
      orangeCircleImage: '../../../../assets/about-company/social-responsibility/orange-icon.png'
    },
    {
      id: 4,
      number: 'SDG 5',
      title: 'Gender Equality',
      subtitle: '',
      blueIconImage: '../../../../assets/about-company/social-responsibility/gender-equality.png',
      orangeCircleImage: '../../../../assets/about-company/social-responsibility/orange-icon.png'
    },
    {
      id: 5,
      number: 'SDG 8',
      title: 'Decent Work &',
      subtitle: 'Economic Growth',
      blueIconImage: '../../../../assets/about-company/social-responsibility/decent-work.png',
      orangeCircleImage: '../../../../assets/about-company/social-responsibility/orange-icon.png'
    },
    {
      id: 6,
      number: 'SDG 13',
      title: 'Climate Action',
      subtitle: '',
      blueIconImage: '../../../../assets/about-company/social-responsibility/climate-action.png',
      orangeCircleImage: '../../../../assets/about-company/social-responsibility/orange-icon.png'
    }
  ];
  impactMetrics: ImpactMetric[] = [
    {
      id: 1,
      type: 'tangible',
      title: 'Tangible Impact:',
      metrics: [
        '100+ lives benefitted from our blood donation camps.',
        '300+ school children supported through the midday meal scheme in Karnataka.',
        '10+ families assisted with rations and clothes.'
      ],
      imagePath: '../../../../assets/about-company/social-responsibility/Our-CSR-Philosophy.png',
      textColor: 'black'
    },
    {
      id: 2,
      type: 'ripple',
      title: 'Ripple Impact:',
      metrics: [
        'Employees and their families motivated to contribute beyond the workplace.',
        'Communities inspired through shared stories on social media.',
        'Long-term partnerships with NGOs driving sustained change.'
      ],
      imagePath: '../../../../assets/about-company/social-responsibility/Ripple-Impact.png',
      textColor: 'white'
    }
  ];
}
